"use client";

import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./Card";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/Select";

import usePaginationParams from "@/hooks/usePaginationParams";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import DataTablePagination from "./DataTablePagination";
import { Skeleton } from "./Skeleton";
import { useTranslation } from "react-i18next";

export function DataTable({
  columns,
  title,
  description,
  actions,
  wrapperClassName,
  tableClassName,
  hideTable = false,
  paginationMode = "server",
  hideFooter = false,
  isLoading,
  data,
  rowCount,
  loadingSkeltonClassName,
  pageParamKey,
  pageSizeParamKey,
  loadingColumnsCount,
  onPaginationModelChange,
  page,
  pageSize,
  headerClassName,
  actionClassName,
  footerContainerClassName = "",
  pageCountRowClassName = "",
  tableRowClassName = "",
  // Remove the default t prop
}) {
  const { t } = useTranslation("ui-components");

  const {
    page: pageHook,
    pageSize: pageSizeHook,
    onPaginationModelChange: hookOnPaginationModelChange,
  } = usePaginationParams(pageParamKey, pageSizeParamKey);
  const [rowCountMemoized, setRowCountMemoized] = useState(rowCount);

  const pagination = {
    pageIndex: page || pageHook,
    pageSize: pageSize || pageSizeHook,
  };

  onPaginationModelChange =
    onPaginationModelChange || hookOnPaginationModelChange;

  const paginationModeProps =
    paginationMode === "server"
      ? {
          manualPagination: true,
        }
      : {
          getPaginationRowModel: getPaginationRowModel(),
          manualPagination: false,
        };

  const table = useReactTable({
    data: data || [],
    columns: columns.map(column => ({
      ...column,
      // A grouped header has no accessorKey, so it names itself with an explicit id.
      id:
        column.id ||
        (column.accessorKey || column.header).toLowerCase().replace(/\s/g, "_"),
    })),
    rowCount: rowCountMemoized,
    getCoreRowModel: getCoreRowModel(),
    autoResetPageIndex: false,
    onPaginationChange: updater => {
      const newPaginationValue =
        updater instanceof Function ? updater(pagination) : updater;
      onPaginationModelChange(newPaginationValue);
    },
    state: {
      pagination,
    },
    ...paginationModeProps,
  });

  const hasFooter = columns.some(column => column.footer);

  useEffect(() => {
    if (![undefined, null].includes(rowCount)) setRowCountMemoized(rowCount);
  }, [rowCount]);

  const pageSizeCurrent = table.getState().pagination.pageSize;
  const pageIndexCurrent = table.getState().pagination.pageIndex;
  const pageCount = table.getPageCount();

  const rowLabel =
    pageSizeCurrent <= 10 ? t("data_table.rows") : t("data_table.row");

  return (
    <Card className={wrapperClassName}>
      <CardHeader
        className={cn(
          "flex flex-row items-center space-y-0 px-7",
          !title && !description && !actions && "pb-0",
          headerClassName
        )}
      >
        <div className="grid gap-2">
          <CardTitle className="leading-8">{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </div>
        <div className={cn("ms-auto flex items-center gap-2", actionClassName)}>
          {actions}
        </div>
      </CardHeader>
      {!hideTable && (
        <CardContent>
          <Table className={tableClassName}>
            <TableHeader>
              {table.getHeaderGroups().map(headerGroup => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map(header => {
                    return (
                      // colSpan carries a grouped header across its children. Flat
                      // tables report 1, so this changes nothing for them.
                      <TableHead key={header.id} colSpan={header.colSpan}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              t(header.column.columnDef.header),
                              header.getContext()
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>

            <TableBody>
              {isLoading ? (
                Array(
                  loadingColumnsCount || table.getState().pagination.pageSize
                )
                  .fill({})
                  .map((_, index) => (
                    <TableRow key={index}>
                      <TableCell colSpan={table.getAllLeafColumns().length}>
                        <Skeleton
                          className={cn("w-full h-10", loadingSkeltonClassName)}
                        />
                      </TableCell>
                    </TableRow>
                  ))
              ) : table?.getRowModel().rows?.length ? (
                table.getRowModel().rows.map(row => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    className={
                      typeof tableRowClassName === "function"
                        ? tableRowClassName(row?.original)
                        : tableRowClassName
                    }
                  >
                    {row.getVisibleCells().map(cell => (
                      <TableCell
                        className={cell.column.columnDef.cellClassName}
                        key={cell.id}
                      >
                        {cell.column.columnDef.cell
                          ? flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )
                          : cell.getValue()}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={table.getAllLeafColumns().length}
                    className="h-24 text-center"
                  >
                    {t("data_table.no_data")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            {hasFooter && (
              <TableFooter>
                {table.getFooterGroups().map(footerGroup => (
                  <TableRow key={footerGroup.id}>
                    {footerGroup.headers.map(header => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.footer,
                              header.getContext()
                            )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableFooter>
            )}
          </Table>

          {!hideFooter && (
            <div
              className={cn(
                "flex items-center justify-center gap-4 xl:gap-8 lg:px-2 max-xl:flex-col-reverse xl:justify-end mt-6",
                footerContainerClassName
              )}
            >
              <div
                className={cn(
                  "flex items-center justify-between gap-8 max-xl:w-full",
                  pageCountRowClassName
                )}
              >
                <div className="flex items-center gap-2">
                  <Select
                    value={`${pageSizeCurrent}`}
                    onValueChange={value => {
                      table.setPageSize(Number(value));
                    }}
                  >
                    <SelectTrigger className="h-8 w-[70px]">
                      <SelectValue placeholder={`${pageSizeCurrent}`} />
                    </SelectTrigger>
                    <SelectContent side="top">
                      {[10, 20, 30, 40, 50].map(pageSize => (
                        <SelectItem key={pageSize} value={`${pageSize}`}>
                          {pageSize}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <p className="text-sm hidden md:block font-medium">
                    {rowLabel} {t("data_table.per_page")}
                  </p>
                </div>

                <div className="flex max-w-[130px] items-center justify-center text-sm font-medium">
                  <span>{t("data_table.page")} &nbsp;</span>
                  {pageIndexCurrent + 1} {t("data_table.of")} {pageCount}
                </div>
              </div>

              <DataTablePagination
                table={table}
                pageIndex={pagination?.pageIndex}
              />
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
